import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, Col, Input, Row, Table } from "reactstrap";
import { FilterSelect, ServiceThreadSpinner } from ".";
import { DesktopContainer } from "../layouts";
import { api, constants, filterHelpers, UserContext } from "../utils";
import _ from "lodash";

export default function MobileRwoCreate(props) {
   const location = useLocation();
   const navigate = useNavigate();
   const { currentUser } = useContext(UserContext);
   const ALL_OPTION = { value: 0, label: "N/A" };
   const ALL_PROBLEMS = { value: 0, label: "-- Select --" };
   const [filters, setFilters] = useState([
      { filterName: "problemId", value: ALL_PROBLEMS },
      { filterName: "problemSubId", value: ALL_OPTION },
   ]);
   const [data, setData] = useState(null);
   const [loaded, setLoaded] = useState(false);
   const [noEdit, setDisabled] = useState(true);
   const [problems, setProblems] = useState([]);
   const [problemSubs, setProblemSubs] = useState([]); // problem children filtered by parent problem id
   const [problemTemps, setProblemTemps] = useState([]); // problem children (subs) unfiltered

   useEffect(() => {
      if (!currentUser) return;
      var active = true; // Indicator of if we only show active problems and their children

      // Get possible drop-down options
      api
         .fetch(`Repair/LoadRwoDropdowns/${active}`)
         .then((response) => {
            if (response.data.success) {
               const problemList = _.concat(
                  [ALL_PROBLEMS],
                  _.map(response.data.problems, (pblm) => ({
                     value: pblm.problemId,
                     label: pblm.problem,
                  }))
               );
               const problemSubList = _.concat(
                  _.map(response.data.problemSubs, (pSub) => ({
                     value: pSub.id,
                     label: pSub.problem,
                     parent: pSub.problemId,
                  }))
               );
               return {
                  problemList: problemList,
                  problemSubList: problemSubList,
               };
            } else {
               throw new Error(response);
            }
         })
         .then(({ problemList, problemSubList }) => {
            setProblems(problemList);
            setProblemTemps(problemSubList);
            // This should only fill in after a parent problem is selected
            setProblemSubs({ value: 0, label: "N/A", parent: 0 });
            runQuery();
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(error);
         });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [currentUser]);

   function runQuery() {
      let dto = location.state.model;
      //console.log(dto);
      if (dto) {
         api
            .post(`Repair/InitRepairFromScan/`, dto)
            .then((response) => {
               if (response.data.success) {
                  setDisabled(!response.data.canEdit);
                  setData(response.data.rwo);
               } else {
                  // The server's sentence (e.g. a label whose machine type no longer exists), never a blank form.
                  toast.error(response.data.message || "The scanned label could not be read.");
               }
            })
            .catch((error) => {
               api.catchHandler(error);
               toast.error(error);
            })
            .finally(() => {
               setLoaded(true);
            });
      }
      else {
         setLoaded(true);
         toast.error("The scanned QR code data failed to be retrieved.");
      }
   }

   function onChange(field, value) {
      let changes = Object.assign({}, data);
      changes[field] = value;
      setData(changes);
   }

   // These are related to the problem type selection change and its sub filter
   const onProblemChange = async (newFilterValue) => {
      // Fill in the problem child drop-down with proper values
      const pblm = _.find(problems, (x) => x.value === newFilterValue.value);
      let newSubList = await fixProblemSubs(newFilterValue.value);

      // Set the problem filters
      var subs = _.pickBy(problemTemps, (e) =>
         _.includes([newFilterValue.value], e.parent)
      );
      const subFilter = _.find(newSubList, (s) => s.value === 0);

      let newFilters = filterHelpers.getFilters(filters, {
         filterName: "problemId",
         value: pblm,
      });
      newFilters = filterHelpers.getFilters(newFilters, {
         filterName: "problemSubId",
         value: subFilter,
      });
      setFilters(newFilters);

      // Set the data values
      let changes = Object.assign({}, data);
      changes.problemId = newFilterValue.value;
      changes.problem = pblm.label;
      changes.problemSubId = subs ? ALL_PROBLEMS.value : ALL_OPTION.value;
      changes.problemChild = "";
      setData(changes);
   };

   const fixProblemSubs = async (id) => {
      let newPblmList = [ALL_OPTION]; // If no children found then N/A is the default option
      var subs = _.pickBy(problemTemps, (e) => _.includes([id], e.parent));
      if (subs) {
         // There are child problems for the selected problem type
         newPblmList = _.concat(
            [ALL_PROBLEMS],
            _.map(subs, (s) => ({ value: s.value, label: s.label }))
         );
         setProblemSubs(newPblmList);
      }
      return newPblmList;
   };

   function onProblemSubChange(newFilterValue) {
      let newFilters = filterHelpers.getFilters(filters, newFilterValue);
      setFilters(newFilters);

      const pblm = _.find(problemSubs, (x) => x.value === newFilterValue.value);
      let changes = Object.assign({}, data);
      changes.problemSubId = newFilterValue.value;
      changes.problemChild = pblm.label;
      setData(changes);
   }

   // Save changes to database
   function saveWorkOrder() {
      let warnings = [];

      if (!data.problemId) {
         warnings.push("Problem is required\n");
      }
      if (data.problemId && problemSubs.length > 1 && !data.problemSubId) {
         warnings.push("Child problem is required\n");
      }
      if (!data.operatorInitials) {
         warnings.push("Lead person initials is required\n");
      }
      if (warnings.length) {
         toast.error(warnings.join(''));
         return;
      }

      api
         .post(`Repair/CreateRepairWorkOrder`, data)
         .then((response) => {
            if (response.data.success) {
               toast.success("New work order was created successfully!");
               // Saved, but the maintenance email failed - say so, so nobody re-submits a saved RWO
               if (response.data.emailWarning)
                  toast.warning(response.data.emailWarning, { autoClose: false });
               navigate(constants.ENDPOINTS.mobileRwoSearch);
            } else {
               toast.error(response.data.message);
            }
         })
         .catch(api.catchHandler);
   }

   return (
      <DesktopContainer screenName="Repair Work Order">
         <ServiceThreadSpinner on={!loaded}>
            <div>
               <Row>
                  <Col xs={12}>
                     <label className="h6 fw-bold">
                        Work Order #: &nbsp;&nbsp;Determined upon save
                     </label>
                  </Col>
               </Row>
               <Row className="align-items-center my-1">
                  <Col className="h6 fw-bold my-0" xs={12}>
                     Machine Type: {data?.machineName || "Error"}
                  </Col>
               </Row>
               <Row className="lineheight8">&nbsp;</Row>
               <Row className="align-items-center my-1">
                  <Col className="h6 fw-bold my-0" xs={12}>
                     Machine #: {data?.machineNumber || "Error"}
                  </Col>
               </Row>
               <Row className="lineheight8">&nbsp;</Row>
               <Row className="align-items-center my-1">
                  <Col className="h6 fw-bold my-0" xs={12}>
                     Side: {data?.side || "N/A"}
                  </Col>
               </Row>
               <Row className="lineheight8">&nbsp;</Row>
               <Row className="align-items-center my-1">
                  <Col className="h6 fw-bold my-0" xs={12}>
                     Spindle #: {data?.spindle || "Error"}
                  </Col>
               </Row>
               <Row className="lineheight8">&nbsp;</Row>
               <Row className="align-items-center my-1">
                  <Col className="h6 fw-bold my-0" xs={12}>
                     Problem:
                  </Col>
               </Row>
               <Row className="align-items-center my-1">
                  <Col xs={12}>
                     <FilterSelect
                        filterName="problemId"
                        options={problems}
                        value={filterHelpers.getValue(filters, "problemId")}
                        onChangeCallback={(e) => onProblemChange(e)}
                     />
                  </Col>
               </Row>
               {problemSubs && problemSubs.length >= 1 ? (
                  <div>
                     <Row className="lineheight8">&nbsp;</Row>
                     <Row className="align-items-center my-1">
                        <Col className="h6 fw-bold my-0" xs={12}>
                           Child Problem:
                        </Col>
                     </Row>
                     <Row className="align-items-center my-1">
                        <Col xs={12}>
                           <FilterSelect
                              filterName="problemSubId"
                              options={problemSubs}
                              value={filterHelpers.getValue(filters, "problemSubId")}
                              onChangeCallback={(e) => onProblemSubChange(e)}
                           />
                        </Col>
                     </Row>
                  </div>
               ) : null}
               <Row className="lineheight8">&nbsp;</Row>
               <Row>
                  <Col xs={12} className="py-0 px-3">
                     <label className="h6 fw-bold my-0">Lead Initials:</label>&nbsp;
                     <Input
                        className="w-25"
                        name="operatorInitials"
                        type="text"
                        maxLength={2}
                        value={
                           data && data.operatorInitials ? data.operatorInitials : ""
                        }
                        onChange={(e) => {
                           onChange("operatorInitials", e.target.value);
                        }}
                     />
                  </Col>
               </Row>
               <Row className="lineheight8">&nbsp;</Row>
               <Row className="align-items-center my-1">
                  <Col className="h6 fw-bold my-0" xs={12}>
                     Additional Comments:
                  </Col>
               </Row>
               <Row className="align-items-center my-1">
                  <Col xs={12}>
                     <Input
                        type="textarea"
                        className="serviceThreadTextArea"
                        value={(data && data.comments) ?? ""}
                        maxLength={500}
                        onChange={(e) => {
                           onChange("comments", e.target.value);
                        }}
                     />
                  </Col>
               </Row>
               {!noEdit ? (
                  <div className="blueBox w-100 my-3">
                     <Row>
                        <Col xs={12}>
                           <h2 align="center" className="text-white mobileBlueBgAlt">
                              Mechanics Comments
                           </h2>
                        </Col>
                     </Row>
                     <Table borderless size="sm">
                        <tbody>
                           <tr>
                              <td className="data-cell my-0 py-1 px-3">
                                 <label className="h6 fw-bold">
                                    <Input
                                       name="closed"
                                       type="checkbox"
                                       className="me-2"
                                       checked={data && data.closed}
                                       onChange={(e) => {
                                          onChange("closed", e.target.checked);
                                       }}
                                    />{" "}
                                    Repair Complete?
                                 </label>
                              </td>
                           </tr>
                           <tr>
                              <td className="data-cell m-0 p-0 lineheight8">
                                 &nbsp;
                              </td>
                           </tr>
                           <tr>
                              <td className="data-cell my-0 py-0 px-3">
                                 <label className="h6 fw-bold my-0">Tech Initials:</label>
                                 <Input
                                    className="w-25"
                                    name="mechanicInitials"
                                    type="text"
                                    maxLength={2}
                                    value={
                                       data && data.mechanicInitials
                                          ? data.mechanicInitials
                                          : ""
                                    }
                                    onChange={(e) => {
                                       onChange("mechanicInitials", e.target.value);
                                    }}
                                 />
                              </td>
                           </tr>
                           <tr>
                              <td className="data-cell m-0 p-0 lineheight8">
                                 &nbsp;
                              </td>
                           </tr>
                           <tr>
                              <td className="data-cell my-0 py-0 px-3">
                                 <label className="h6 fw-bold mx-1 my-0">
                                    Repair Comments:
                                 </label>
                              </td>
                           </tr>
                           <tr>
                              <td className="data-cell my-0 py-0 px-3">
                                 <Input
                                    className="serviceThreadTextArea"
                                    type="textarea"
                                    name="repairComments"
                                    value={data?.repairComments ?? ""}
                                    maxLength={500}
                                    onChange={(e) => {
                                       onChange("repairComments", e.target.value);
                                    }}
                                 />
                              </td>
                           </tr>
                        </tbody>
                     </Table>
                  </div>
               ) : null}
               <Row className="lineheight8">&nbsp;</Row>
               <Row>
                  <Button
                     title="Save"
                     className="shredButton ms-2 btn-md"
                     onClick={() => saveWorkOrder()}
                  >
                     Save
                  </Button>
               </Row>
               <Row className="lineheight8">&nbsp;</Row>
               <Row>
                  <Button
                     title="To Search"
                     className="btn ms-2 btn-md"
                     onClick={() => navigate(`/mobile-rwo-search`)}
                  >
                     To Search
                  </Button>
               </Row>
               <br />
            </div>
         </ServiceThreadSpinner>
      </DesktopContainer>
   );
}
